import React, { useState, useEffect } from "react";
import "./joyride.css";
import axios from "axios";
import config from "../../config";
import uniq from "lodash/uniq";
import moment from "moment";
import JoyRideForm from "./JoyRideForm";
import { useHistory, useLocation } from "react-router-dom";

const getSlotDescription = (schedule) => {
  if (schedule.description) {
    return schedule.description;
  }

  if (schedule?.arrivalTime?.length) {
    return `Slot timings are from ${moment(
      schedule.arrivalTime[0],
      "HH:mm"
    ).format("hh:mm a")}${
      schedule.arrivalTime[1]
        ? ` to ${moment(schedule.arrivalTime[1], "HH:mm").format("hh:mm a")}`
        : ""
    }`;
  }

  return "";
};

const JoyRideComponent = (props) => {
  // eslint-disable-next-line react/prop-types
  const { schedules = [], setTermsnConditions, returnUrl } = props;
  const [paths, setPaths] = useState([]);
  const [localLoader, setLocalLoader] = useState([]);
  const [selected, setSelected] = useState({
    schedule: [],
    date: [],
    index: -1,
  });
  const [error, setError] = useState([]);
  const [payableAmount, setPayableAmount] = useState([]);
  const [availableSeats, setAvailableSeats] = useState([]);
  const [tncChecks, setTncChecks] = useState([]);
  const history = useHistory();
  const location = useLocation();

  const orderByPaths = (schedules) => {
    let newPaths = [...paths];
    schedules.forEach((schedule) => {
      const pathIndex = newPaths.findIndex((p) => p._id === schedule.path._id);
      if (pathIndex === -1) {
        newPaths.push({
          ...schedule.path,
          schedules: [schedule],
          startDate: schedule.effectiveFromDate,
          endDate: schedule.effectiveToDate,
        });
      } else {
        if (
          new Date(schedule.effectiveFromDate) <
          new Date(newPaths[pathIndex].startDate)
        ) {
          newPaths[pathIndex].startDate = schedule.effectiveFromDate;
        }

        if (
          new Date(schedule.effectiveToDate) >
          new Date(newPaths[pathIndex].endDate)
        ) {
          newPaths[pathIndex].endDate = schedule.effectiveToDate;
        }

        newPaths[pathIndex].schedules.push(schedule);
      }
    });
    setPaths(newPaths);
  };

  const handleScheduleChange = (index, event, n, scheduleId) => {
    const scheduleIndex = schedules.findIndex((s) => s._id === scheduleId);
    let newSchedule = [...selected.schedule];
    newSchedule[index] = schedules[scheduleIndex];

    let newLocalLoader = [...localLoader];
    newLocalLoader[index] = 1;
    setSelected({
      ...selected,
      schedule: newSchedule,
      index: index,
    });
    setLocalLoader(newLocalLoader);
  };

  const handleDateChange = (index, selectedDate) => {
    let newDate = [...selected.date];
    newDate[index] = selectedDate;

    let newSchedule = [...selected.schedule];
    newSchedule[index] = null;

    let newAvailableSeats = [...availableSeats];
    newAvailableSeats[index] = null;

    let newPayableAmount = [...payableAmount];
    newPayableAmount[index] = null;

    setSelected({
      ...selected,
      date: newDate,
      schedule: newSchedule,
      index: index,
    });

    setAvailableSeats(newAvailableSeats);
    setPayableAmount(newPayableAmount);
  };

  useEffect(() => {
    if (selected.index !== -1) {
      checkSeatAvailability(selected.index, selected.schedule, selected.date);
    }
  }, [selected]);

  const handleBookAction = (index) => {
    const valid = isFormValid(index);

    if (!valid) {
      return;
    }

    if (!tncChecks[index]) {
      let newError = [...error];
      newError[index] = "Please agree to the terms and conditions.";
      setError(newError);
      return;
    }

    if (valid && availableSeats[index] > 0) {
      const travelDate = moment(selected.date[index]).format("MM/DD/YYYY");

      // Preserve utm_source from current URL
      const params = new URLSearchParams(location.search);
      const utmSource = params.get("utm_source");
      const search = utmSource
        ? `?utm_source=${encodeURIComponent(utmSource)}`
        : "";

      history.push({
        pathname: "/booking",
        search: search,
        state: {
          selectedScheduleObj: selected.schedule[index],
          date: travelDate,
          selectedDateFormatted: travelDate,
          seats: availableSeats[index],
          returnUrl: returnUrl || "/bookwithus",
        },
      });
    }
  };

  const isFormValid = (index) => {
    let formValid = true;
    let newError = [...error];

    if (!selected.date[index]) {
      formValid = false;
      newError[index] = "Please select date.";
      setError(newError);
      return formValid;
    }

    if (!selected.schedule[index] || !selected.schedule[index]?._id) {
      formValid = false;
      newError[index] = "Please select a time slot.";
      setError(newError);
      return formValid;
    }

    if (formValid) {
      newError[index] = "";
      setError(newError);
    }
    return formValid;
  };

  const checkSeatAvailability = (
    index,
    scheduleArray = selected.schedule,
    dateArray = selected.date
  ) => {
    const valid = isFormValid(index);

    if (valid) {
      setTncChecks([]);
      const travelDate = moment(dateArray[index]).format("MM/DD/YYYY");
      axios
        .get(
          `${config.baseUrl}v2/schedules/availability?travelDate=${travelDate}&scheduleId=${scheduleArray[index]._id}`
        )
        .then((res) => {
          let newLocalLoader = [...localLoader];
          newLocalLoader[index] = 0;
          setLocalLoader(newLocalLoader);
          if (res.status === 200) {
            let newAvailableSeats = [...availableSeats];
            let newPayableAmount = [...payableAmount];

            newAvailableSeats[index] = res.data.availableSeats;
            newPayableAmount[index] = res.data?.priceBreakup?.grandTotal;

            setAvailableSeats(newAvailableSeats);
            setPayableAmount(newPayableAmount);
          } else {
            console.log("Something went wrong");
          }
        });
    } else {
      let newLocalLoader = [...localLoader];
      newLocalLoader[index] = 0;
      setLocalLoader(newLocalLoader);
    }
  };

  const onChangeTncCheck = (index, event) => {
    let newTncChecks = [...tncChecks];
    const isChecked = event.target.checked;
    newTncChecks[index] = isChecked;
    setTncChecks(newTncChecks);
    isFormValid(index);
  };

  useEffect(() => {
    orderByPaths(schedules);
  }, [schedules]);

  if (paths.length === 0) {
    return (
      <div className="schedules-wrapper" style={{ padding: "20px" }}>
        No joyrides available at the moment.
      </div>
    );
  }

  return (
    <div className="schedules-wrapper" style={{ padding: "20px" }}>
      {paths.map((path, index) => (
        <JoyRideForm
          key={path._id}
          path={path}
          index={index}
          selected={selected}
          getSlotDescription={getSlotDescription}
          isLoading={localLoader[index]}
          handleDateChange={(date) => handleDateChange(index, date)}
          handleScheduleChange={(event, n, scheduleId) =>
            handleScheduleChange(index, event, n, scheduleId)
          }
          handleBookAction={() => handleBookAction(index)}
          availableSeats={availableSeats}
          payableAmount={payableAmount}
          error={error}
          tncChecks={tncChecks}
          onClickTnc={() =>
            setTermsnConditions(
              uniq(
                path.schedules
                  .map((schedule) => schedule.ticketNotes || [])
                  .flat()
              )
            )
          }
          onChangeTncCheck={(e) => onChangeTncCheck(index, e)}
        />
      ))}
    </div>
  );
};

export default JoyRideComponent;
